import React, { useState } from "react";
import { FaPlus, FaUmbrellaBeach } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import StatusChip from "../../../components/StatusChip/StatusChip";
import Loader from "../../../components/Loader/Loader";
import { useStudentLeave } from "./hooks/useStudentLeave";
import { useStudentSession } from "../context/StudentSessionContext";
import ApplyLeaveModal from "./components/ApplyLeaveModal";
import "./StudentLeave.css";

// The backend's exact status spelling isn't pinned down, so map case
// insensitively and fall back to a neutral tone for anything unexpected.
const STATUS_TONE = {
  pending: "warning",
  approved: "success",
  accepted: "success",
  rejected: "danger",
  declined: "danger",
};

const getStatusTone = (status) => STATUS_TONE[String(status || "").trim().toLowerCase()] || "secondary";

// Leave list items may come back with any of a few reasonable field-name
// variants depending on the backend serializer - try each in order.
const pick = (item, keys, fallback = "") => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const StudentLeave = () => {
  const { student } = useStudentSession();
  const { leaves, isLoading, isSubmitting, submitLeave } = useStudentLeave(
    student?.id,
    student?.student_program?.program_id,
    student?.student_program?.session_id
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="student-leave">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h5 className="sp-section-title mb-0">My Leaves</h5>
        <Button icon={<FaPlus />} onClick={() => setIsModalOpen(true)}>
          Apply Leave
        </Button>
      </div>

      {isLoading ? (
        <Loader />
      ) : leaves.length === 0 ? (
        <div className="sp-card student-leave-empty text-center">
          <FaUmbrellaBeach size={32} className="mb-2 text-muted" />
          <p className="mb-0 text-muted">You haven&apos;t applied for any leave yet.</p>
        </div>
      ) : (
        <div className="student-leave-list">
          {leaves.map((leave, index) => {
            const status = pick(leave, ["status", "leave_status"], "pending");
            const fromDate = pick(leave, ["from_date", "start_date", "leave_from", "fromDate"]);
            const toDate = pick(leave, ["to_date", "end_date", "leave_to", "toDate"]);
            // Confirmed against the real backend (2026-10-26): the field is
            // apply_reason (matching what applyLeave's own submit payload
            // sends), not the generic "reason" guessed earlier.
            const reason = pick(leave, ["apply_reason", "reason", "remarks", "leave_reason", "description"], "-");
            const tone = getStatusTone(status);
            const key = leave?.id ?? leave?.leave_id ?? index;
            const label = String(status || "Pending");

            return (
              <div key={key} className="sp-card student-leave-row" data-tone={tone}>
                <div className="student-leave-dates">
                  <span className="fw-semibold">
                    {formatDate(fromDate)}
                    {toDate && toDate !== fromDate ? ` - ${formatDate(toDate)}` : ""}
                  </span>
                  <p className="text-muted mb-0 student-leave-reason">
                    <span className="student-leave-reason-label">Reason:</span> {reason}
                  </p>
                </div>
                <StatusChip tone={tone}>{label.charAt(0).toUpperCase() + label.slice(1)}</StatusChip>
              </div>
            );
          })}
        </div>
      )}

      <ApplyLeaveModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={submitLeave}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default StudentLeave;
